// Central RBAC map for the admin panel. Permissions are "resource:action"
// strings matching what /auth/verify returns (superusers get "*:*").
// Backend enforces the same permissions per endpoint (require_permission),
// so this is not security by obscurity — the UI just hides what the API
// would reject anyway.

export interface AdminUser {
  permissions?: string[];
  roles?: { name?: string }[];
  is_superuser?: boolean;
}

/** Required VIEW permission for each admin tab (empty = always visible, e.g. dashboard). */
export const ADMIN_TAB_PERMISSION: Record<string, string> = {
  dashboard: "",
  users: "users:read",
  projects: "projects:read",
  "published-apps": "projects:read",
  roles: "rbac:read",
  billing: "billing:read",
  payments: "billing:read",
  engagements: "engagements:read",
  "support-tickets": "support:read",
  vouchers: "vouchers:read",
  "audit-logs": "audit:read",
  costs: "costs:read",
  referrals: "referrals:read",
  invites: "invites:read",
  influencers: "influencers:read",
};

/** Every permission that grants access to at least one admin tab. */
export const ADMIN_PANEL_PERMISSIONS = Object.values(ADMIN_TAB_PERMISSION).filter(Boolean);

/**
 * Full-access users bypass every per-tab check: platform superusers, holders of
 * the system-admin wildcard, and anyone with the `super_admin` role. This keeps
 * super_admin all-access even when new permissions haven't been granted to its
 * role yet, so adding a feature never silently hides it from super admins.
 */
export function isFullAccessAdmin(user: AdminUser | null | undefined): boolean {
  if (!user) return false;
  if (user.is_superuser) return true;
  const perms = user.permissions || [];
  if (perms.includes("*:*") || perms.includes("system:*") || perms.includes("system:admin")) return true;
  return (user.roles || []).some((r) => r?.name === "super_admin");
}

/**
 * True if the user holds `permission` (exact "resource:action"), the resource
 * wildcard ("resource:*"), or is a full-access admin. Empty permission means
 * "no restriction".
 */
export function hasAdminPermission(user: AdminUser | null | undefined, permission?: string): boolean {
  if (!permission) return true;
  if (isFullAccessAdmin(user)) return true;
  const perms = user?.permissions || [];
  if (perms.includes(permission)) return true;
  const resource = permission.split(":")[0];
  return perms.includes(`${resource}:*`);
}

/** True if the user can access the admin panel at all (full-access or ≥1 admin-panel permission). */
export function canAccessAdminPanel(user: AdminUser | null | undefined): boolean {
  if (isFullAccessAdmin(user)) return true;
  return ADMIN_PANEL_PERMISSIONS.some((p) => hasAdminPermission(user, p));
}
